import { default as React, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  CircularProgress,
} from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import AppsIcon from '@material-ui/icons/Apps';
import { useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import skyStageIcon from '../../../static/skyStageIcon.svg';

/** Derive display name from a @wellsky.com email. e.g. "andi.metts@wellsky.com" → "Andi Metts" */
function emailToName(email: string): string {
  const local = email.split('@')[0];
  return local
    .split(/[._-]/)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/** Two-letter initials from a display name or email. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

interface OwnerInfo {
  email: string;
  name: string;
  initials: string;
}

const AVATAR_COLORS = ['#1a5f5f', '#2a7f7f', '#0a4f4f', '#3a9090'];

/**
 * Full-page Access Denied experience shown when the gateway (oauth2-proxy)
 * redirects an unauthorized user to /access-denied.
 *
 * Query params:
 *   ?app=<entityName>    — used to look up the catalog entity for display name + owners
 *   ?owner=<email>       — fallback owner email if the catalog lookup fails
 */
export const AccessDeniedPage = () => {
  const location = useLocation();
  const catalogApi = useApi(catalogApiRef);

  const params = new URLSearchParams(location.search);
  const appNameParam = params.get('app') || '';
  // owner= may contain up to 4 comma-separated emails (set by the oauth error template).
  // Kept as a single param to stay within URL-length limits of the meta-refresh redirect.
  const fallbackOwnerEmails = (params.get('owner') || '')
    .split(',')
    .map(e => e.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, 4);

  const [displayName, setDisplayName] = useState(appNameParam);
  const [owners, setOwners] = useState<OwnerInfo[]>([]);
  const [loading, setLoading] = useState(!!appNameParam);

  useEffect(() => {
    if (!appNameParam) {
      setLoading(false);
      return;
    }

    catalogApi
      .getEntityByRef(`component:default/${appNameParam}`)
      .then(entity => {
        if (!entity) return;

        setDisplayName(entity.metadata.title || entity.metadata.name);

        const ownersRaw =
          entity.metadata.annotations?.['microapp/owners'] || '';
        const createdBy =
          entity.metadata.annotations?.['microapp/createdBy'] || '';

        const emailSet = new Set<string>();
        ownersRaw
          .split(',')
          .map(e => e.trim().toLowerCase())
          .filter(Boolean)
          .forEach(e => emailSet.add(e));
        if (createdBy.trim()) emailSet.add(createdBy.trim().toLowerCase());
        // Also include URL owner= params — they are written by the oauth error
        // template at deploy time and may contain owners not yet reflected in
        // the catalog annotation (e.g. annotation not yet synced, or the
        // configmap was updated more recently than the last catalog refresh).
        fallbackOwnerEmails.forEach(e => emailSet.add(e));

        if (emailSet.size > 0) {
          setOwners(
            Array.from(emailSet).map(email => {
              const name = emailToName(email);
              return { email, name, initials: getInitials(name) };
            }),
          );
        }
      })
      .catch(() => {
        // Catalog lookup failed — fall back to the owner(s) passed in the URL.
        // The oauth error template encodes up to 4 emails as a comma-separated list.
        if (fallbackOwnerEmails.length > 0) {
          setOwners(
            fallbackOwnerEmails.map(email => {
              const name = emailToName(email);
              return { email, name, initials: getInitials(name) };
            }),
          );
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appNameParam, catalogApi]);

  return (
    // Full-viewport overlay — covers the Backstage sidebar too
    <Box
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background:
          'linear-gradient(135deg, #0a3a3a 0%, #0d5252 45%, #1a7575 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 16px 32px',
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {/* ── SkyStage logo above card ── */}
      <Box
        display="flex"
        alignItems="center"
        style={{ marginBottom: 32, gap: 12 }}
      >
        <img
          src={skyStageIcon}
          alt=""
          aria-hidden="true"
          style={{ height: 44 }}
        />
        <Typography
          style={{
            color: '#ffffff',
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: 0.5,
            lineHeight: 1,
          }}
        >
          <span style={{ fontWeight: 300 }}>Sky</span>Stage
        </Typography>
      </Box>

      {/* ── Main card ── */}
      <Paper
        elevation={6}
        style={{
          padding: '40px 40px 32px',
          maxWidth: 520,
          width: '100%',
          borderRadius: 16,
          textAlign: 'center',
        }}
      >
        {/* Lock icon in amber circle */}
        <Box
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: '#fff3e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
          aria-hidden="true"
        >
          <LockIcon style={{ fontSize: 36, color: '#e65100' }} />
        </Box>

        <Typography
          variant="h5"
          style={{ fontWeight: 700, marginBottom: 12 }}
          role="heading"
          aria-level={1}
        >
          Access Denied
        </Typography>

        <Typography variant="body1" style={{ marginBottom: 8 }}>
          You don't have permission to access{' '}
          <strong>{displayName || appNameParam || 'this application'}</strong>
        </Typography>

        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginBottom: 24 }}
        >
          This app has restricted access. To request access, reach out to one of
          the app owners listed below.
        </Typography>

        {/* ── Owners section ── */}
        {loading && (
          <Box display="flex" justifyContent="center" mb={3}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && owners.length > 0 && (
          <Box
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              overflow: 'hidden',
              textAlign: 'left',
              marginBottom: 28,
            }}
          >
            {/* Section label */}
            <Box
              style={{
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="caption"
                style={{
                  fontWeight: 700,
                  letterSpacing: 1,
                  color: '#757575',
                  textTransform: 'uppercase',
                }}
              >
                App Owners
              </Typography>
            </Box>

            {/* Owner rows */}
            {owners.map((owner, idx) => (
              <Box
                key={owner.email}
                display="flex"
                alignItems="center"
                style={{
                  padding: '12px 16px',
                  borderBottom:
                    idx < owners.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <Avatar
                  style={{
                    backgroundColor: AVATAR_COLORS[idx % AVATAR_COLORS.length],
                    width: 40,
                    height: 40,
                    fontSize: 14,
                    fontWeight: 700,
                    marginRight: 12,
                    flexShrink: 0,
                  }}
                  aria-label={owner.name}
                >
                  {owner.initials}
                </Avatar>
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {owner.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    <a
                      href={`mailto:${owner.email}`}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {owner.email}
                    </a>
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Browse button ── */}
        <Button
          variant="contained"
          href="/apps"
          startIcon={<AppsIcon />}
          style={{
            backgroundColor: '#1a5f5f',
            color: '#fff',
            fontWeight: 700,
            letterSpacing: 1.5,
            padding: '10px 28px',
            borderRadius: 8,
            textTransform: 'uppercase',
          }}
        >
          Browse All Apps
        </Button>
      </Paper>

      {/* ── Footer ── */}
      <Typography
        variant="caption"
        style={{
          marginTop: 28,
          color: 'rgba(255, 255, 255, 0.55)',
          textAlign: 'center',
        }}
      >
        Powered by SkyStage — WellSky's Internal Developer Portal
      </Typography>
    </Box>
  );
};
